/**
 * Guest Wishlist Functionality
 * Uses localStorage to store wishlist items for non-logged-in users
 */
class WishlistManager {
  constructor() {
    this.storageKey = 'mondocosmetica_wishlist';
    this.wishlist = this.loadWishlist();
    this.isUpdating = false;
    this.observer = null;
    this.init();
  }

  init() {
    // Initialize wishlist buttons on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.updateAllWishlistButtons();
      });
    } else {
      this.updateAllWishlistButtons();
    }

    // Handle dynamic content (e.g., AJAX-loaded products)
    // Use debounce to prevent infinite loops
    let updateTimeout;
    const observer = new MutationObserver((mutations) => {
      // Skip if already updating to prevent loops
      if (this.isUpdating) return;

      // Only process if nodes were actually added (not just attribute changes)
      const hasNewNodes = mutations.some(mutation => 
        mutation.addedNodes.length > 0 && 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && 
          ((node.hasAttribute && node.hasAttribute('data-wishlist-product-id')) ||
          (node.querySelector && node.querySelector('[data-wishlist-product-id]')))
        )
      );

      if (hasNewNodes) {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          this.isUpdating = true;
          // Disconnect observer temporarily to prevent loop
          observer.disconnect();
          
          this.updateAllWishlistButtons();
          
          // Reconnect observer after updates
          setTimeout(() => {
            this.isUpdating = false;
            if (document.body) {
              observer.observe(document.body, {
                childList: true,
                subtree: true
              });
            }
          }, 100);
        }, 200);
      }
    });

    // Only start observing after DOM is ready
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      });
    }

    // Store observer for cleanup if needed
    this.observer = observer;
  }

  loadWishlist() {
    if (typeof Storage === 'undefined') return [];
    
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    if (typeof Storage === 'undefined') return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  isInWishlist(productId) {
    return this.wishlist.includes(String(productId));
  }

  addToWishlist(productId) {
    const id = String(productId);
    if (!this.isInWishlist(id)) {
      this.wishlist.push(id);
      this.saveWishlist();
      this.updateWishlistButton(productId);
      this.showNotification(window.wishlistStrings?.added || 'Added to wishlist');
      this.dispatchWishlistEvent('wishlist:added', { productId: id });
    }
  }

  removeFromWishlist(productId) {
    const id = String(productId);
    const index = this.wishlist.indexOf(id);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      this.updateWishlistButton(productId);
      this.showNotification(window.wishlistStrings?.removed || 'Removed from wishlist');
      this.dispatchWishlistEvent('wishlist:removed', { productId: id });
    }
  }

  toggleWishlist(productId) {
    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
    } else {
      this.addToWishlist(productId);
    }
  }

  updateWishlistButton(productId) {
    const buttons = document.querySelectorAll(`[data-wishlist-product-id="${productId}"]`);
    buttons.forEach(button => {
      const isInWishlist = this.isInWishlist(productId);
      button.classList.toggle('wishlist-button--active', isInWishlist);
      button.setAttribute('aria-pressed', isInWishlist);
      if (window.wishlistStrings) {
        button.setAttribute('aria-label', isInWishlist 
          ? window.wishlistStrings.removeLabel 
          : window.wishlistStrings.addLabel);
      }
      
      // Update icon if present
      const icon = button.querySelector('.wishlist-icon');
      if (icon) {
        icon.innerHTML = isInWishlist 
          ? `<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 18.35l-1.45-1.32C3.4 12.36 0 9.28 0 5.5 0 2.42 2.42 0 5.5 0c1.74 0 3.41.81 4.5 2.09C11.09.81 12.76 0 14.5 0 17.58 0 20 2.42 20 5.5c0 3.78-3.4 6.86-8.55 11.54L10 18.35z"/></svg>`
          : `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor"><path d="M10 18.35l-1.45-1.32C3.4 12.36 0 9.28 0 5.5 0 2.42 2.42 0 5.5 0c1.74 0 3.41.81 4.5 2.09C11.09.81 12.76 0 14.5 0 17.58 0 20 2.42 20 5.5c0 3.78-3.4 6.86-8.55 11.54L10 18.35z" stroke-width="1.5"/></svg>`;
      }
    });
  }

  updateAllWishlistButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-product-id]');
    buttons.forEach(button => {
      const productId = button.getAttribute('data-wishlist-product-id');
      if (!productId) return;

      this.updateWishlistButton(productId);

      // Add click handler if not already added
      if (!button.hasAttribute('data-wishlist-initialized')) {
        button.setAttribute('data-wishlist-initialized', 'true');
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleWishlist(productId);
        });
      }
    });
  }

  showNotification(message) {
    // Create or get notification element
    let notification = document.getElementById('wishlist-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'wishlist-notification';
      notification.className = 'wishlist-notification';
      document.body.appendChild(notification);
    }

    notification.textContent = message;
    notification.classList.add('wishlist-notification--show');

    setTimeout(() => {
      notification.classList.remove('wishlist-notification--show');
    }, 3000);
  }

  dispatchWishlistEvent(eventName, detail) {
    const event = new CustomEvent(eventName, { detail });
    document.dispatchEvent(event);
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  getAllProductIds() {
    return [...this.wishlist];
  }
}

// Initialize wishlist manager
if (typeof window !== 'undefined') {
  window.wishlistManager = new WishlistManager();
}


